import { fhirR4 } from "@smile-cdr/fhirts";

import { formatAddress, formatHumanName, getAccountInviteDate } from "./fhir-utils";

export const FHIR_RELATIONSHIP_ROLE_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-RoleCode";
export const COHABITANT_EXTENSION_URL = "http://vitall.com/fhir/ext/rp_cohabitant";

export enum FhirRelationshipCode {
  OTHER = "OTHER",
  SPOUSE = "SPS",
  PARTNER = "SIGOTHR",
  CHILD = "CHILD",
  PARENT = "PRN",
  SIBLING = "SIB",
  FRIEND = "FRND",
  GUARDIAN = "GUARD",
}

// NOTE: These are the old code for relationships that are not part of the `FHIR_RELATIONSHIP_SYSTEM` but were used in the legacy system.
// Want to keep them here in case we need to map legacy data.
export enum FhirLegacyRelationshipCode {
  OTHER_LEGACY = "NOK",
  PARTNER_LEGACY = "PTNT",
  FRIEND_LEGACY = "FAMMEMB",
}

/**
 * Utility function that returns all possible `FhirRelationshipCode` values.
 *
 * @returns A list of all possible `FhirRelationshipCode` values.
 */
export const getAllFhirRelationshipCodes = (): FhirRelationshipCode[] => {
  return Object.values(FhirRelationshipCode);
};

/**
 * Utility function that returns a translation key string for the provided `FhirRelationshipCode`.
 *
 * @param relationshipCode - The `FhirRelationshipCode` to provide a translation key for.
 * @returns A string representing the translation key for the provided `relationshipCode`.
 */
export const translationKeyForRelationship = (relationshipCode: FhirRelationshipCode): string => {
  return `pages.patientOverview.careGivers.relationshipRoles.${relationshipCode}`;
};

/**
 * English display string for each relationship code. The display written to a FHIR resource MUST be
 * English so a record saved during a translated session does not persist a localized label for every
 * user. The UI renders the relationship from the code, so this display is only a stored fallback.
 * Kept in sync with `en/common.json` relationshipRoles by a test.
 */
export const RELATIONSHIP_ENGLISH_DISPLAY: Record<FhirRelationshipCode, string> = {
  [FhirRelationshipCode.OTHER]: "Other",
  [FhirRelationshipCode.SPOUSE]: "Spouse",
  [FhirRelationshipCode.PARTNER]: "Partner",
  [FhirRelationshipCode.CHILD]: "Child",
  [FhirRelationshipCode.PARENT]: "Parent",
  [FhirRelationshipCode.SIBLING]: "Sibling",
  [FhirRelationshipCode.FRIEND]: "Friend",
  [FhirRelationshipCode.GUARDIAN]: "Guardian",
};

/**
 * Returns the English display string for a relationship code.
 *
 * @param relationshipCode - The `FhirRelationshipCode` to resolve.
 * @returns The English display, or the empty string for an unknown code.
 */
export const englishDisplayForRelationship = (relationshipCode: FhirRelationshipCode): string => {
  return RELATIONSHIP_ENGLISH_DISPLAY[relationshipCode] ?? "";
};

/**
 * Utility function that returns a `fhirR4.CodeableConcept` for the provided `FhirRelationshipCode`.
 *
 * @param relationshipCode - The `FhirRelationshipCode` to convert to a `fhirR4.CodeableConcept`.
 * @returns A `fhirR4.CodeableConcept` with the `relationshipCode` as the `code` and the English display string as the `display`.
 */
export const createCodeableConceptFromRelationship = (
  relationshipCode: FhirRelationshipCode
): fhirR4.CodeableConcept => {
  return {
    coding: [
      {
        system: FHIR_RELATIONSHIP_ROLE_SYSTEM,
        code: relationshipCode,
        display: englishDisplayForRelationship(relationshipCode),
      },
    ],
  };
};

/**
 * Returns the first `FhirRelationShipCode` found in the `relationship` array.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @returns The resulting `FhirRelationShipCode` or undefined if it doesn't exist.
 * @remarks This function assumes that the `FhirRelationshipCode` is in the array of codings in the first `CodeableConcept` in the `relationship` array.
 */
export const getCaregiverRelationship = (person: fhirR4.RelatedPerson): FhirRelationshipCode | undefined => {
  const relationshipCodeableConcept = person.relationship?.[0];

  if (!relationshipCodeableConcept?.coding || relationshipCodeableConcept.coding.length === 0) {
    return undefined;
  }

  const coding = relationshipCodeableConcept.coding.find(
    aCoding =>
      aCoding.system === FHIR_RELATIONSHIP_ROLE_SYSTEM && aCoding.code && (aCoding.code as FhirRelationshipCode)
  );
  return coding?.code as FhirRelationshipCode | undefined;
};

/**
 * Returns a copy of the `CodeableConcept[]` in `relationship` of `RelatedPerson` and updates or adds the `relationshipCode` value in it.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @param relationshipCode - The relationshipCode to set for the caregiver.
 * @returns The resulting `CodeableConcept[]` with the value `relationshipCode` saved and an English display.
 * @remarks This function does not modify the input `caregiver` object, it returns a new `CodeableConcept[]` to set in the `relationship` parameter.
 */
export const getUpdatedRelationshipSettingRelationshipCode = (
  caregiver: fhirR4.RelatedPerson,
  relationshipCode: FhirRelationshipCode
): fhirR4.CodeableConcept[] => {
  const existingRelationship = getCaregiverRelationship(caregiver);

  // No changed needed
  if (existingRelationship === relationshipCode) {
    return caregiver.relationship ?? [];
  }

  const relationshipCodeableConcept: fhirR4.CodeableConcept[] = caregiver.relationship ? caregiver.relationship : [];

  // Find existing coding to update
  const existingCodingIndex = relationshipCodeableConcept.findIndex(
    rc => rc.coding && rc.coding.some(c => c.system === FHIR_RELATIONSHIP_ROLE_SYSTEM)
  );

  // Update existing coding or add new one
  if (existingCodingIndex !== -1) {
    relationshipCodeableConcept[existingCodingIndex] = createCodeableConceptFromRelationship(relationshipCode);
  } else {
    relationshipCodeableConcept.push(createCodeableConceptFromRelationship(relationshipCode));
  }

  return relationshipCodeableConcept;
};

/**
 * Returns the first `phone` value in the `telecom` array.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @returns The resulting `phone` value or the empty string.
 */
export const getCaregiverPhoneNumber = (caregiver: fhirR4.RelatedPerson): string => {
  return caregiver.telecom?.find(t => t.system === "phone")?.value ?? "";
};

/**
 * Returns the first `email` value in the `telecom` array.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @returns The resulting `email` value or the empty string.
 */
export const getCaregiverEmail = (caregiver: fhirR4.RelatedPerson): string => {
  return caregiver.telecom?.find(t => t.system === "email")?.value ?? "";
};

/**
 * Returns the `isCohabitant` value found in the `extension` of the `RelatedPerson`.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @returns The resulting `isCohabitant` value or undefined if it doesn't exist.
 */
export const isCaregiverACohabitant = (caregiver: fhirR4.RelatedPerson): boolean | undefined => {
  return caregiver.extension?.find(ext => ext.url === COHABITANT_EXTENSION_URL)?.valueBoolean;
};

/**
 * Returns a copy of the `Extension[]` in `extension` of `RelatedPerson` and updates or adds the `isCohabitant` value in it.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @param isCohabitant - Whether the caregiver lives with the patient.
 * @returns The resulting `Extension[]` array with the value `isCohabitant` saved.
 * @remarks This function does not modify the input `caregiver` object, it returns a new `Extension[]`.
 */
export const getUpdatedExtensionSettingIsCohabitant = (caregiver: fhirR4.RelatedPerson, isCohabitant: boolean) => {
  const extensions = caregiver.extension != undefined ? caregiver.extension : [];
  const existingExtensionIndex = extensions.findIndex(ext => ext.url === COHABITANT_EXTENSION_URL);

  if (existingExtensionIndex !== -1) {
    extensions[existingExtensionIndex] = {
      ...extensions[existingExtensionIndex],
      valueBoolean: isCohabitant,
    };
  } else {
    extensions.push({
      url: COHABITANT_EXTENSION_URL,
      valueBoolean: isCohabitant,
    });
  }

  return extensions;
};

/**
 * Extract all the information from a FHIR RelatedPerson (Caregiver) resource.
 * @param caregiver - The FHIR RelatedPerson resource.
 * @returns The extracted information.
 */
export const extractInfoFromCaregiver = (caregiver: fhirR4.RelatedPerson) => {
  const id = caregiver?.id ?? "";
  const nameFull = formatHumanName(caregiver?.name?.[0] as fhirR4.HumanName, {
    lineSeparator: " ",
  });
  const nameFamilyGiven = formatHumanName(caregiver?.name?.[0] as fhirR4.HumanName, {
    include: ["family", "given"],
  });
  const nameGivenFamily = formatHumanName(caregiver?.name?.[0] as fhirR4.HumanName, {
    include: ["given", "family"],
    lineSeparator: " ",
  });
  const nameFirst = formatHumanName(caregiver?.name?.[0] as fhirR4.HumanName, {
    include: ["first"],
    includeTrailingWhitespace: true,
  });
  const nameMiddle = formatHumanName(caregiver?.name?.[0] as fhirR4.HumanName, {
    include: ["middle"],
    includeTrailingWhitespace: true,
  });
  const nameFamily = formatHumanName(caregiver?.name?.[0] as fhirR4.HumanName, {
    include: ["family"],
    includeTrailingWhitespace: true,
  });

  const birthDate = caregiver?.birthDate as fhirR4.Date;
  const caregiverContact = caregiver?.telecom as fhirR4.ContactPoint[];
  const email = getCaregiverEmail(caregiver);
  const phone = getCaregiverPhoneNumber(caregiver);
  const caregiverAddress = caregiver?.address?.[0] as fhirR4.Address;
  const fullAddress = caregiverAddress && formatAddress(caregiverAddress);
  const addressLine1 = caregiverAddress && formatAddress(caregiverAddress, { include: ["line"] });
  const addressLine2 =
    caregiverAddress &&
    formatAddress(caregiverAddress, {
      include: ["city", "state", "postalCode"],
    });
  const gender = caregiver?.gender;
  const isCohabitant = isCaregiverACohabitant(caregiver);
  const relationship = getCaregiverRelationship(caregiver);
  const accountInviteDate = getAccountInviteDate(caregiver);

  return {
    id,
    nameFull,
    nameFamilyGiven,
    nameGivenFamily,
    nameFirst,
    nameMiddle,
    nameFamily,
    birthDate,
    caregiverContact,
    email,
    phone,
    caregiverAddress,
    fullAddress,
    addressLine1,
    addressLine2,
    gender,
    isCohabitant,
    relationship,
    accountInviteDate,
  };
};

/**
 * Returns the translation key used to translate the options for the `Does the caregiver live with the patient` question.
 * @param isCohabitant - Whether the caregiver lives with the patient.
 * @returns The translation key.
 */
export const getIsCohabitantTranslationKey = (isCohabitant: boolean): string => {
  if (isCohabitant) {
    return "pages.patientOverview.careGivers.cohabitantYes";
  } else {
    return "pages.patientOverview.careGivers.cohabitantNo";
  }
};
